import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { Container, Form, Row, Col, Button } from 'react-bootstrap';
import zhTW from 'date-fns/locale/zh-TW';
import api from '../../services/api';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CoachScheduleCalendar.css';
import { useParams, useNavigate } from 'react-router-dom';
import { atob } from 'js-base64';
import { FaArtstation } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip,
  Fade 
} from '@mui/material';
import { 
  Home as HomeIcon 
} from '@mui/icons-material';

const locales = {
  'zh-TW': zhTW,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const statusColors = {
  OPEN: '#28a745',     // 綠色
  BOOKED: '#dc3545',   // 紅色
  RESERVED: 'peru', // peru
  EMPTY: 'peru',   // peru
  MOVING: '#265985',   // 深藍色
  NONE: 'white',     // 白色
};

const statusLabels = {
  OPEN: '空閒',
  BOOKED: '被約',
  RESERVED: '預留',
  EMPTY: '預留',
  MOVING: '移動',
};

const ScheduleOfCoach = () => {
  const [schedules, setSchedules] = useState([]);
  const [coachInfo, setCoachInfo] = useState(null);
  const { coachId: hashedId, month } = useParams();
  const { scheduleData, setScheduleData } = useAuth();
  const navigate = useNavigate();
  let coachId;
  // 設定當前日期
  const [currentDate, setCurrentDate] = useState(() => {
    if (month) {
      //console.log('month = ', month);
      const [year, monthNum] = month.split('-');
      const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
      
      // 驗證日期是否有效
      if (isNaN(date.getTime())) {
        return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      }
      return date;
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });
  // 設定默認視圖為月曆
  const [viewMode, setViewMode] = useState(window.innerWidth < 400 ? 'agenda' : 'month');
  // 設定是否為手機版
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  try {
    coachId = hashedId ? atob(hashedId) : null;
  } catch (error) {
    console.error('Invalid Base64 string:', error);
    coachId = null;
  }

  useEffect(() => {
    setScheduleData({ coachHashedId: hashedId, month: month });
  }, [hashedId, month, setScheduleData]);

  // 獲取教練資訊
  useEffect(() => {
    const fetchCoachInfo = async () => {
      try {
        const response = await api.get(`/coaches/${coachId}/`, {
          headers: {
            'Accept': 'application/json',
          },
          withCredentials: false  // 對於公開頁面，不需要發送認證信息
        });
        setCoachInfo(response.data);
      } catch (error) {
        console.error('Error fetching coach info:', error);
        // 不要在錯誤時觸發登出
      }
    };

    if (coachId) {
      fetchCoachInfo();
    }
  }, [coachId]);

  // 提取成獨立函數
  const fetchSchedules = async (firstDay, lastDay) => {
    try {
      // 格式化日期為 YYYY-MM-DD
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      //console.log(formatDate(firstDay));
      //console.log(formatDate(lastDay));
      
      const response = await api.get(
        `/schedules/public_schedules/`, {
          params: {
            coach: coachId,
            start_date: formatDate(firstDay),
            end_date: formatDate(lastDay)
          },
          headers: {
            'Accept': 'application/json',
          },
          withCredentials: false
        }
      );

      //console.log(response.data);

      const formattedSchedules = response.data.map(schedule => {
        // 如果同一天有上午和下午的課程，只顯示上午的課程
        if(schedule.time_slot === 'AFTERNOON'){
          const morningSchedule = response.data.find(s => 
            s.date === schedule.date && s.time_slot === 'MORNING'
          );
          if(morningSchedule) {
            return null;
          }
        }
        
        const timeSlotMap = {
          'MORNING': ['09:00', '12:00'],
          'AFTERNOON': ['13:00', '16:00'],
        };
        
        const [startTime, endTime] = timeSlotMap[schedule.time_slot] || ['00:00', '00:00'];
        
        const startDate = new Date(`${schedule.date}T${startTime}:00`);
        const endDate = new Date(`${schedule.date}T${endTime}:00`);

        return {
          id: schedule.id,
          title: schedule.location,
          start: startDate,
          end: endDate,
          status: schedule.status,
          coach_name: schedule.coach_name,
          time_slot_display: schedule.time_slot_display,
        };
      }).filter(Boolean);

      //console.log('Total formatted schedules:', formattedSchedules.length);

      // 生成整個月的日期陣列
      const generateDatesForMonth = (start, end) => {
        const dates = [];
        const current = new Date(start);
        
        while (current <= end) {
          dates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        return dates;
      };

      const allDates = generateDatesForMonth(firstDay, lastDay);
      
      // 將 API 返回的行程轉換為以日期為鍵的映射
      const scheduleMap = formattedSchedules.reduce((acc, schedule) => {
        const dateKey = format(schedule.start, 'yyyy-MM-dd');
        acc[dateKey] = schedule;
        return acc;
      }, {});

      // 為每一天創建行程（如果沒有行程則創建空行程）
      const completeSchedules = allDates.map(date => {
        const dateKey = format(date, 'yyyy-MM-dd');
        if (scheduleMap[dateKey]) {
          return scheduleMap[dateKey];
        }
        
        // 創建空行程
        return {
          id: `empty-${dateKey}`,
          title: '無行程',
          start: new Date(date.setHours(9, 0, 0)),
          end: new Date(date.setHours(12, 0, 0)),
          status: 'NONE',
          coach_name: coachInfo?.username || '',
          time_slot_display: '上午',
        };
      });

      setSchedules(completeSchedules);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  // 在 useEffect 中使用
  useEffect(() => {
    if (coachId) {
      const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
      fetchSchedules(firstDay, lastDay);
    }
  }, [coachId, currentDate]);

  // 自定義事件樣式
  const eventStyleGetter = (event) => {
    let style = {
      backgroundColor: statusColors[event.status],
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0',
      display: 'block',
      padding: '2px 5px',
      margin: '2px 0',
    };
    
    return {
      style,
    };
  };

  // 監聽螢幕寬度變化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const CustomToolbar = (toolbar) => {
    const navigate = (action) => {
      toolbar.onNavigate(action);
    };

    return (
      <div className="rbc-toolbar">
        <span className="rbc-btn-group">
          <button type="button" onClick={() => navigate('PREV')}>
            上個月
          </button>
          <button type="button" onClick={() => navigate('NEXT')}>
            下個月
          </button>
        </span>
        <span className="rbc-toolbar-label">
          {format(toolbar.date, 'yyyy年MM月', { locale: zhTW })}
        </span>
        <div className="rbc-btn-group" style={{ fontSize:'1rem' }} >
          <button 
            className={toolbar.view === 'month' ? 'rbc-active' : ''}
            onClick={() => toolbar.onView('month')}
          >
            月曆
          </button>
          <button 
            className={toolbar.view === 'agenda' ? 'rbc-active' : ''}
            onClick={() => toolbar.onView('agenda')}
          >
            列表
          </button>
        </div>
      </div>
    );
  };

  // 手機版的自定義工具列
  const MobileToolbar = (toolbar) => {
    const navigate = (action) => {
      toolbar.onNavigate(action);
    };

    return (
      <div className="rbc-toolbar mobile-toolbar">
        <div className="toolbar-nav">
          <button onClick={() => navigate('PREV')}>◀</button>
          <span>{format(toolbar.date, 'yyyy年MM月', { locale: zhTW })}</span>
          <button onClick={() => navigate('NEXT')}>▶</button>
        </div>
        <div className="rbc-btn-group" style={{ fontSize:'0.8rem' }} >
          <button 
            className={toolbar.view === 'month' ? 'rbc-active' : ''}
            onClick={() => toolbar.onView('month')}
          >
            月曆
          </button>
          <button 
            className={toolbar.view === 'agenda' ? 'rbc-active' : ''}
            onClick={() => toolbar.onView('agenda')}
          >
            列表
          </button>
        </div>
      </div>
    );
  };
  
  const handleNavigate = (date, mode, action) => {
    let newDate;
    if (action === 'PREV') {
      const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      newDate = prevMonth;
      setCurrentDate(prevMonth);
    } else {
      const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
      newDate = nextMonth;
      setCurrentDate(nextMonth);
    }

    // 更新 URL
    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, '0');
    navigate(`/public/coach/${hashedId}/schedule/${year}-${month}`);
    
    const firstDay = new Date(newDate.getFullYear(), newDate.getMonth() - 1, 1);
    const lastDay = new Date(newDate.getFullYear(), newDate.getMonth() + 2, 0);
    fetchSchedules(firstDay, lastDay);
  };

  // 添加自定義列表視圖格式化器
  const formats = {
    weekdayFormat: 'EEE',
    dateFormat: 'dd',
    dayFormat: 'dd',
    monthHeaderFormat: 'yyyy年MM月',
    // 添加中文星期格式
    dayHeaderFormat: (date) => {
      const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
      return weekdays[date.getDay()];
    },
    // 添加列表視圖的日期格式
    dayRangeHeaderFormat: ({ start, end }) => {
      return `${format(start, 'MM月dd日')} - ${format(end, 'MM月dd日')}`;
    },
    // 添加列表視圖的日期格式
    agendaDateFormat: (date) => {
      return format(date, 'MM月dd日');
    },
    // 添加列表視圖的時間格式
    agendaTimeFormat: (date) => {
      return format(date, 'HH:mm');
    },
  };

  // 自定義列表視圖的事件渲染
  const components = {
    toolbar: isMobile ? MobileToolbar : CustomToolbar,
    agenda: {
      event: ({ event }) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', padding: '4px' }}>
            <div>
              <span style={{ marginLeft: '8px' }}>{event.title}</span>
            </div>
          </div>
        );
      },
      time: () => null,  // 隱藏時間列
      date: ({ label }) => {
        //console.log('label:', label); // 檢查 label 的值
        return <div style={{ flex: '1' }}>{label}</div>;
      },
      header: () => null,
    },
    month: {
      header: ({ date }) => {
        const weekdays = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
        return <span>{weekdays[date.getDay()]}</span>;
      }
    }
  };

  // 計算當月天數
  const getDaysInMonth = (date) => {
    let daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    //console.log("daysInMonth", daysInMonth);
    return daysInMonth;
  };

  // 過濾月曆視圖中的事件
  const filterEvents = (events) => {
    return events.filter(event => {
      // 只顯示有狀態的事件,不顯示 'NONE' 狀態的事件
      return event.status !== 'NONE';
    });
  };

  // 根據視圖類型決定是否過濾事件
  const getFilteredEvents = () => {
    if (viewMode === 'month') {
      return filterEvents(schedules);
    }
    return schedules;
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4
    }}>
      <Container maxWidth="lg">
        {/* 返回首頁按鈕 */}
        <Fade in timeout={500}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
            <Tooltip title="返回首頁 ASA-滑雪裝忙協會">
              <IconButton
                href="/"
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                  }
                }}
              >
                <HomeIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Fade>

        {/* 現有的日曆內容 */}
        <Paper 
          elevation={8}
          sx={{ 
            p: 4,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.3)'
          }}
        >
          <h2 className="mb-2">
            {coachInfo ? `${coachInfo.username} 行程表` : '行程表'}
          </h2>

          <div className="text-center mb-2">
            <p className="mb-2">
              一起滑雪嗎？
              <Button variant="primary" href="/login" className="mx-2">立即登入</Button>
              <Button variant="outline-primary" href="/register">註冊新帳號</Button>
            </p>
          </div>

          {/* status label */}
          <div className="schedule-legend">
            {Object.entries(statusLabels).map(([key, label]) => {
              if(key !== 'EMPTY'){
                return (
                  <div key={key} className="legend-item">
                    <span 
                      className="legend-color" 
                      style={{ backgroundColor: statusColors[key] }}
                    ></span>
                    <span>{label}</span>
                  </div>
                );
              }
            })}
          </div>
          
          <Calendar
            localizer={localizer}
            events={getFilteredEvents(schedules)}
            views={['month', 'agenda']}
            view={viewMode}
            onView={setViewMode}
            onNavigate={handleNavigate}
            startAccessor="start"
            endAccessor="end"
            defaultView="month"  // 設置默認視圖為月曆
            eventPropGetter={eventStyleGetter}
            components={components}
            formats={formats}
            length={getDaysInMonth(currentDate)}  // 動態設定當月天數
            date={currentDate}
            //showMultiDayTimes={true}
            messages={{
              month: '月曆',
              agenda: '列表',
              previous: '上一頁',
              next: '下一頁',
              today: '今天',
              noEventsInRange: '此期間沒有課程',
              showMore: total => `+${total} 更多`
            }}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default ScheduleOfCoach; 